import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { FileText, Download, Loader2, CheckCircle, AlertCircle, Sparkles, ChevronDown, User, LogOut } from 'lucide-react';
import axios from 'axios';
import DemoContent from './components/DemoContent';
import FAQGenerator from './components/FAQGenerator';
import BattlecardGenerator from './components/BattlecardGenerator';
import ArchitectureGenerator from './components/ArchitectureGenerator';
import CustomerSalesCallSimulator from './components/CustomerSalesCallSimulator';
import SalesCallConversation from './components/SalesCallConversation';
import LMSCourseCreator from './components/LMSCourseCreator';
import PowerPointGenerator from './components/PowerPointGenerator';
import GameMaker from './components/GameMaker';
import ContentAnalytics from './components/ContentAnalytics';
import Logo from './components/Logo';
import InteractiveBackground from './components/InteractiveBackground';
import CountUp from './components/CountUp';
import Login from './components/Login';
import UserProfile from './components/UserProfile';
import EmailVerification from './components/EmailVerification';
import PasswordReset from './components/PasswordReset';
import AppPasswordProtection from './components/AppPasswordProtection';
import Footer from './components/Footer';
import PrivacyPolicy from './components/PrivacyPolicy';
import TrustSecurity from './components/TrustSecurity';
import { CreditProvider, useCredits } from './contexts/CreditContext';
import { ApiKeyProvider, useApiKey } from './contexts/ApiKeyContext';
import { SessionProvider, useSession } from './contexts/SessionContext';
import CreditCounter from './components/CreditCounter';
import { getApiUrl } from './config';
import AgentFeedbackDashboard from './components/AgentFeedbackDashboard';
import CustomerAnalyticsDashboard from './components/CustomerAnalyticsDashboard';
import AgentAnalyticsDashboard from './components/AgentAnalyticsDashboard';
import CustomerManager from './components/CustomerManager';
import AgentPromptEditor from './components/AgentPromptEditor';
import AgentManager from './components/AgentManager';
import AgentSelfHealing from './components/AgentSelfHealing';
import AnimatedBackground from './components/AnimatedBackground';
import ApiKeyStatus from './components/ApiKeyStatus';
import UserApiKeyManager from './components/UserApiKeyManager';
import ToolDocumentation from './components/ToolDocumentation';
import WebhookManager from './components/WebhookManager';
import ConsoleWrapper from './components/ConsoleWrapper';
import OnboardingGuide from './components/OnboardingGuide.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import PerformanceMonitor from './components/PerformanceMonitor.jsx';

// Protected Route Component
const ProtectedRoute = ({ children, requireAuth = true }) => {
  const { user, isLoading } = useSession();
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          <span>Loading...</span>
        </div>
      </div>
    );
  }
  
  if (requireAuth && !user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Main App Layout Component
const AppLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, setUser, isLoading: sessionLoading } = useSession();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const [showDocumentation, setShowDocumentation] = useState(false);
  const [currentToolForDocs, setCurrentToolForDocs] = useState('');
  
  const agenticDropdownRef = useRef(null);
  const enablementDropdownRef = useRef(null);
  const [isAgenticDropdownOpen, setIsAgenticDropdownOpen] = useState(false);
  const [isEnablementDropdownOpen, setIsEnablementDropdownOpen] = useState(false);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (agenticDropdownRef.current && !agenticDropdownRef.current.contains(event.target)) {
        setIsAgenticDropdownOpen(false);
      }
      if (enablementDropdownRef.current && !enablementDropdownRef.current.contains(event.target)) {
        setIsEnablementDropdownOpen(false);
      }
    };

    if (isAgenticDropdownOpen || isEnablementDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAgenticDropdownOpen, isEnablementDropdownOpen]);

  const handleLogout = useCallback(async () => {
    setIsLoggingOut(true);
    try {
      await fetch(getApiUrl('/logout'), {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      navigate('/');
      sessionStorage.removeItem('appPasswordVerified');
      setTimeout(() => {
        setIsLoggingOut(false);
      }, 1000);
    }
  }, [setUser, navigate]);

  const handleUserMenuToggle = useCallback(() => {
    if (!showUserMenu) {
      const button = document.querySelector('[data-user-button]');
      if (button) {
        const rect = button.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        
        if (spaceAbove > spaceBelow && spaceAbove > 150) {
          setDropdownPosition('top');
        } else {
          setDropdownPosition('bottom');
        }
      }
    }
    setShowUserMenu(!showUserMenu);
  }, [showUserMenu]);

  const handleAgenticDropdownToggle = useCallback((e) => {
    e.stopPropagation();
    setIsAgenticDropdownOpen(!isAgenticDropdownOpen);
  }, [isAgenticDropdownOpen]);

  const handleAgenticDropdownItemClick = useCallback((path) => {
    navigate(path);
    setIsAgenticDropdownOpen(false);
  }, [navigate]);

  const isGeneratorPage = useMemo(() => 
    ['/tools/ppt', '/tools/faq', '/tools/battlecard', '/tools/architecture', '/analytics', '/tools/game', '/tools/sales-sim', '/tools/lms'].includes(location.pathname), 
    [location.pathname]
  );

  const isAgenticPage = useMemo(() => 
    ['/analytics/feedback', '/analytics/customers', '/analytics/agents', '/admin/customers', '/tools/prompts', '/admin/agents', '/admin/self-healing', '/admin/webhooks', '/admin/api-keys'].includes(location.pathname), 
    [location.pathname]
  );

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <PerformanceMonitor />
      
      {/* Header */}
      <header className="bg-black/80 backdrop-blur-md border-b border-gray-800 sticky top-0 relative z-50 min-h-[72px]">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between w-full">
            {/* Logo */}
            <Logo onClick={() => navigate('/')} />
            
            {/* Navigation */}
            <nav className="flex items-center space-x-6 flex-shrink-0 min-w-fit">
              <button
                onClick={() => navigate('/')}
                className={`px-6 py-2 rounded-lg font-semibold ${location.pathname === '/' ? 'bg-purple-600 text-white' : 'text-white hover:bg-gray-800'} transition-colors duration-300`}
              >
                Home
              </button>
              
              {/* Enablement Generation Tooling Dropdown - HIDDEN */}
              {/* <div className="relative" ref={enablementDropdownRef}>
                <button
                  onClick={handleEnablementDropdownToggle}
                  className={`px-4 py-2 rounded-lg font-semibold flex items-center space-x-1 ${
                    isGeneratorPage ? 'bg-purple-600 text-white' : 'text-white hover:bg-gray-800'
                  } transition-colors duration-300`}
                >
                  <span>Enablement Generation Tooling</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isEnablementDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isEnablementDropdownOpen && (
                  <div 
                    className="absolute top-full left-0 mt-1 w-64 bg-gray-900 border border-gray-700 rounded-lg shadow-lg z-[99999] pointer-events-auto"
                    style={{ 
                      zIndex: 99999,
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      marginTop: '4px',
                      width: '256px',
                      backgroundColor: '#111827',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.3)',
                      pointerEvents: 'auto'
                    }}
                  >
                    <div className="py-1">
                      <div
                        onClick={() => handleEnablementDropdownItemClick('ppt')}
                        className={`block w-full px-4 py-2 text-left hover:bg-gray-800 cursor-pointer transition-colors select-none ${
                          page === 'ppt' ? 'bg-purple-600 text-white font-semibold' : 'text-white'
                        }`}
                        style={{ userSelect: 'none' }}
                      >
                        PowerPoint Generator
                      </div>
                      <div
                        onClick={() => handleEnablementDropdownItemClick('faq')}
                        className={`block w-full px-4 py-2 text-left hover:bg-gray-800 cursor-pointer transition-colors select-none ${
                          page === 'faq' ? 'bg-purple-600 text-white font-semibold' : 'text-white'
                        }`}
                        style={{ userSelect: 'none' }}
                      >
                        FAQ Generator
                      </div>
                      <div
                        onClick={() => handleEnablementDropdownItemClick('battlecard')}
                        className={`block w-full px-4 py-2 text-left hover:bg-gray-800 cursor-pointer transition-colors select-none ${
                          page === 'battlecard' ? 'bg-purple-600 text-white font-semibold' : 'text-white'
                        }`}
                        style={{ userSelect: 'none' }}
                      >
                        Battlecard Generator
                      </div>
                      <div
                        onClick={() => handleEnablementDropdownItemClick('architecture')}
                        className={`block w-full px-4 py-2 text-left hover:bg-gray-800 cursor-pointer transition-colors select-none ${
                          page === 'architecture' ? 'bg-purple-600 text-white font-semibold' : 'text-white'
                        }`}
                        style={{ userSelect: 'none' }}
                      >
                        Architecture Generator
                      </div>
                      <div
                        onClick={() => handleEnablementDropdownItemClick('analytics')}
                        className={`block w-full px-4 py-2 text-left hover:bg-gray-800 cursor-pointer transition-colors select-none ${
                          page === 'analytics' ? 'bg-purple-600 text-white font-semibold' : 'text-white'
                        }`}
                        style={{ userSelect: 'none' }}
                      >
                        Content Analytics
                      </div>
                      <div
                        onClick={() => handleEnablementDropdownItemClick('game')}
                        className={`block w-full px-4 py-2 text-left hover:bg-gray-800 cursor-pointer transition-colors select-none ${
                          page === 'game' ? 'bg-purple-600 text-white font-semibold' : 'text-white'
                        }`}
                        style={{ userSelect: 'none' }}
                      >
                        Game Maker
                      </div>
                      <div
                        onClick={() => handleEnablementDropdownItemClick('sales-sim')}
                        className={`block w-full px-4 py-2 text-left hover:bg-gray-800 cursor-pointer transition-colors select-none ${
                          page === 'sales-sim' ? 'bg-purple-600 text-white font-semibold' : 'text-white'
                        }`}
                        style={{ userSelect: 'none' }}
                      >
                        Sales Call Simulator
                      </div>
                      <div
                        onClick={() => handleEnablementDropdownItemClick('lms')}
                        className={`block w-full px-4 py-2 text-left hover:bg-gray-800 cursor-pointer transition-colors select-none ${
                          page === 'lms' ? 'bg-purple-600 text-white font-semibold' : 'text-white'
                        }`}
                        style={{ userSelect: 'none' }}
                      >
                        LMS Course Creator
                      </div>
                    </div>
                  </div>
                )}
              </div> */}
              
              
              
              {/* Agentic AI Tools Dropdown */}
              <div className="relative" ref={agenticDropdownRef}>
                <button
                  onClick={handleAgenticDropdownToggle}
                  className={`px-6 py-2 rounded-lg font-semibold flex items-center space-x-2 ${
                    isAgenticPage ? 'bg-purple-600 text-white' : 'text-white hover:bg-gray-800'
                  } transition-colors duration-300`}
                >
                  <span>Agentic AI Tools</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isAgenticDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isAgenticDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-gray-900 border border-gray-700 rounded-lg shadow-lg z-[99999] pointer-events-auto">
                    <div className="py-1">
                      <div
                        onClick={() => handleAgenticDropdownItemClick('/analytics/feedback')}
                        className={`block w-full px-4 py-2 text-left hover:bg-gray-800 cursor-pointer transition-colors select-none ${
                          location.pathname === '/analytics/feedback' ? 'bg-purple-600 text-white font-semibold' : 'text-white'
                        }`}
                      >
                        Agent Feedback Dashboard
                      </div>
                      <div
                        onClick={() => handleAgenticDropdownItemClick('/analytics/customers')}
                        className={`block w-full px-4 py-2 text-left hover:bg-gray-800 cursor-pointer transition-colors select-none ${
                          location.pathname === '/analytics/customers' ? 'bg-purple-600 text-white font-semibold' : 'text-white'
                        }`}
                      >
                        Customer Analytics Dashboard
                      </div>
                      <div
                        onClick={() => handleAgenticDropdownItemClick('/analytics/agents')}
                        className={`block w-full px-4 py-2 text-left hover:bg-gray-800 cursor-pointer transition-colors select-none ${
                          location.pathname === '/analytics/agents' ? 'bg-purple-600 text-white font-semibold' : 'text-white'
                        }`}
                      >
                        Agent Performance Dashboard
                      </div>
                      {user && user.is_admin && (
                        <div
                          onClick={() => handleAgenticDropdownItemClick('/admin/customers')}
                          className={`block w-full px-4 py-2 text-left hover:bg-gray-800 cursor-pointer transition-colors select-none ${
                            location.pathname === '/admin/customers' ? 'bg-purple-600 text-white font-semibold' : 'text-white'
                          }`}
                        >
                          Customer Manager
                        </div>
                      )}
                      <div
                        onClick={() => handleAgenticDropdownItemClick('/tools/prompts')}
                        className={`block w-full px-4 py-2 text-left hover:bg-gray-800 cursor-pointer transition-colors select-none ${
                          location.pathname === '/tools/prompts' ? 'bg-purple-600 text-white font-semibold' : 'text-white'
                        }`}
                      >
                        Edit Agent Prompts
                      </div>
                      <div
                        onClick={() => handleAgenticDropdownItemClick('/admin/agents')}
                        className={`block w-full px-4 py-2 text-left hover:bg-gray-800 cursor-pointer transition-colors select-none ${
                          location.pathname === '/admin/agents' ? 'bg-purple-600 text-white font-semibold' : 'text-white'
                        }`}
                      >
                        Manage My Agents
                      </div>
                      <div
                        onClick={() => handleAgenticDropdownItemClick('/admin/self-healing')}
                        className={`block w-full px-4 py-2 text-left hover:bg-gray-800 cursor-pointer transition-colors select-none ${
                          location.pathname === '/admin/self-healing' ? 'bg-purple-600 text-white font-semibold' : 'text-white'
                        }`}
                      >
                        Agent Self-Healing
                      </div>
                      <div
                        onClick={() => handleAgenticDropdownItemClick('/admin/webhooks')}
                        className={`block w-full px-4 py-2 text-left hover:bg-gray-800 cursor-pointer transition-colors select-none ${
                          location.pathname === '/admin/webhooks' ? 'bg-purple-600 text-white font-semibold' : 'text-white'
                        }`}
                      >
                        Webhook Management
                      </div>
                      <div
                        onClick={() => handleAgenticDropdownItemClick('/admin/api-keys')}
                        className={`block w-full px-4 py-2 text-left hover:bg-gray-800 cursor-pointer transition-colors select-none ${
                          location.pathname === '/admin/api-keys' ? 'bg-purple-600 text-white font-semibold' : 'text-white'
                        }`}
                      >
                        My API Keys
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
            </nav>

            {/* User Authentication */}
            <div className="flex items-center space-x-4 flex-shrink-0 min-w-fit">
              {sessionLoading ? (
                <div className="flex items-center space-x-2 px-4 py-2 rounded-lg font-semibold text-white">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Loading...</span>
                </div>
              ) : user ? (
                <div className="relative flex items-center space-x-4">
                  {/* Console Button */}
                  <button
                    onClick={() => navigate('/console')}
                    className="flex items-center space-x-2 px-4 py-2 rounded-lg font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors duration-300"
                  >
                    <span>Console</span>
                  </button>
                  
                  <button
                    data-user-button
                    onClick={handleUserMenuToggle}
                    className="flex items-center space-x-2 px-4 py-2 rounded-lg font-semibold text-white hover:bg-gray-800 transition-colors duration-300"
                  >
                    <User className="h-5 w-5" />
                    <span>{user.username}</span>
                    {user.is_admin && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-900 text-purple-300">
                        Founder
                      </span>
                    )}
                  </button>
                  {/* Credit Counter - HIDDEN */}
                  {/* {!user.is_admin && (
                    <div className="w-40">
                      <CreditCounter />
                    </div>
                  )} */}
                  <ApiKeyStatus />
                  {showUserMenu && (
                    <div className={`absolute right-0 w-48 bg-gray-900 border border-gray-700 rounded-md shadow-lg py-1 z-50 ${
                      dropdownPosition === 'top' 
                        ? 'bottom-full mb-2' 
                        : 'top-full mt-2'
                    }`}>
                      <button
                        onClick={() => {
                          navigate('/profile');
                          setShowUserMenu(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-gray-800 transition-colors"
                      >
                        Profile
                      </button>
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-gray-800 transition-colors"
                      >
                        Sign Out
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            await fetch(getApiUrl('/force-logout'), {
                              method: 'POST',
                              credentials: 'include'
                            });
                            setUser(null);
                            setShowUserMenu(false);
                          } catch (error) {
                            console.error('Force logout failed:', error);
                          }
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-orange-400 hover:bg-gray-800 transition-colors"
                      >
                        Force Logout (Debug)
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => navigate('/login')}
                  className="flex items-center space-x-2 px-4 py-2 rounded-lg font-semibold text-white hover:bg-gray-800 transition-colors duration-300"
                >
                  <User className="h-5 w-5" />
                  <span>Sign In</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 relative z-10 flex-1">
        {children}
      </div>
      
      {/* Tool Documentation Window */}
      <ToolDocumentation
        toolName={currentToolForDocs}
        isVisible={showDocumentation}
        onClose={() => setShowDocumentation(false)}
        onToggle={() => setShowDocumentation(!showDocumentation)}
      />
      
      {/* Footer */}
      <Footer />
      
      {/* Onboarding Guide */}
      <OnboardingGuide 
        isOpen={false} // Removed as per new_code
        onClose={() => {}} // Removed as per new_code
        onComplete={() => {}} // Removed as per new_code
      />
    </div>
  );
};

// Home Page Component
const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useSession();

  return (
    <div className="max-w-6xl mx-auto pb-16">
      {/* Hero Section */}
      <div className="text-center mb-20">
        <div className="mb-12">
          <h1 className="text-7xl font-extrabold mb-8 bg-gradient-to-r from-purple-400 via-blue-400 to-purple-400 bg-clip-text text-transparent tracking-tight leading-tight">
            Your AI Agents Just Got Smarter
          </h1>
          <p className="text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed font-light">
            Deploy self-improving AI agents with feedback loops, evaluation tooling, and performance dashboards.
          </p>
        </div>
        
        {/* Primary CTA */}
        <div className="mb-16">
          <button
            onClick={() => navigate('/analytics/agents')}
            className="group relative bg-gradient-to-r from-purple-600 to-blue-600 text-white px-12 py-5 rounded-full font-semibold text-xl hover:from-purple-700 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 shadow-2xl shadow-purple-500/25"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-transparent to-blue-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-full"></div>
            <span className="relative">Launch Agent Tools</span>
          </button>
        </div>
      </div>

      {/* Core Feature Blocks */}
      <div className="mb-16 relative">
        <div className="absolute inset-0 w-screen left-1/2 transform -translate-x-1/2 -z-20">
          <AnimatedBackground />
        </div>
        <h2 className="text-4xl font-bold text-center mb-16 text-white tracking-tight relative z-10">
          Intelligent Agent Infrastructure
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative z-10">
          {/* Agent Feedback Tracker */}
          <div className="group relative overflow-hidden rounded-2xl bg-[#ffffff0d] backdrop-blur-xl border border-white/10 hover:border-purple-500/30 transition-all duration-500 hover:shadow-2xl hover:shadow-purple-500/20 z-10">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative p-8">
              <div className="flex items-center mb-6">
                <div className="relative">
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mr-5 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-purple-500/25">
                    <svg className="w-7 h-7 text-white group-hover:animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="absolute -inset-1 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl blur opacity-0 group-hover:opacity-30 transition-opacity duration-300"></div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-1 tracking-tight">Agent Feedback Tracker</h3>
                  <p className="text-sm text-gray-400 font-medium">Real-time evaluation</p>
                </div>
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed text-sm">
                Real-time agent evaluation and logging with comprehensive feedback loops for continuous improvement.
              </p>
              <button
                onClick={() => navigate('/analytics/feedback')}
                className="inline-flex items-center text-purple-400 font-semibold hover:text-purple-300 transition-all duration-300 group-hover:translate-x-1"
              >
                View Tool 
                <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Prompt Editor */}
          <div className="group relative overflow-hidden rounded-2xl bg-[#ffffff0d] backdrop-blur-xl border border-white/10 hover:border-blue-500/30 transition-all duration-500 hover:shadow-2xl hover:shadow-blue-500/20 z-10">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-cyan-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative p-8">
              <div className="flex items-center mb-6">
                <div className="relative">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mr-5 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-blue-500/25">
                    <svg className="w-7 h-7 text-white group-hover:animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </div>
                  <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl blur opacity-0 group-hover:opacity-30 transition-opacity duration-300"></div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-1 tracking-tight">Prompt Editor</h3>
                  <p className="text-sm text-gray-400 font-medium">System optimization</p>
                </div>
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed text-sm">
                Edit and experiment with system prompts to optimize agent performance and behavior.
              </p>
              <button
                onClick={() => navigate('/tools/prompts')}
                className="inline-flex items-center text-blue-400 font-semibold hover:text-blue-300 transition-all duration-300 group-hover:translate-x-1"
              >
                Get Started 
                <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Self-Healing System */}
          <div className="group relative overflow-hidden rounded-2xl bg-[#ffffff0d] backdrop-blur-xl border border-white/10 hover:border-emerald-500/30 transition-all duration-500 hover:shadow-2xl hover:shadow-emerald-500/20 z-10">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 via-transparent to-teal-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative p-8">
              <div className="flex items-center mb-6">
                <div className="relative">
                  <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center mr-5 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-emerald-500/25">
                    <svg className="w-7 h-7 text-white group-hover:animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </div>
                  <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl blur opacity-0 group-hover:opacity-30 transition-opacity duration-300"></div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-1 tracking-tight">Self-Healing System</h3>
                  <p className="text-sm text-gray-400 font-medium">Automatic optimization</p>
                </div>
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed text-sm">
                Automatic or suggested prompt updates based on performance feedback and evaluation metrics.
              </p>
              <button
                onClick={() => navigate('/analytics/agents')}
                className="inline-flex items-center text-emerald-400 font-semibold hover:text-emerald-300 transition-all duration-300 group-hover:translate-x-1"
              >
                View System 
                <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Analytics Dashboard */}
          <div className="group relative overflow-hidden rounded-2xl bg-[#ffffff0d] backdrop-blur-xl border border-white/10 hover:border-purple-500/30 transition-all duration-500 hover:shadow-2xl hover:shadow-purple-500/20 z-10">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-indigo-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative p-8">
              <div className="flex items-center mb-6">
                <div className="relative">
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center mr-5 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-purple-500/25">
                    <svg className="w-7 h-7 text-white group-hover:animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div className="absolute -inset-1 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-xl blur opacity-0 group-hover:opacity-30 transition-opacity duration-300"></div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-1 tracking-tight">Analytics Dashboard</h3>
                  <p className="text-sm text-gray-400 font-medium">Performance insights</p>
                </div>
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed text-sm">
                Performance insights with feedback context to track agent effectiveness and improvement areas.
              </p>
              <button
                onClick={() => navigate('/analytics/agents')}
                className="inline-flex items-center text-purple-400 font-semibold hover:text-purple-300 transition-all duration-300 group-hover:translate-x-1"
              >
                View Dashboard 
                <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Human-in-the-Loop Review */}
          <div className="group relative overflow-hidden rounded-2xl bg-[#ffffff0d] backdrop-blur-xl border border-white/10 hover:border-blue-500/30 transition-all duration-500 hover:shadow-2xl hover:shadow-blue-500/20 z-10">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-sky-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative p-8">
              <div className="flex items-center mb-6">
                <div className="relative">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-sky-600 rounded-xl flex items-center justify-center mr-5 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-blue-500/25">
                    <svg className="w-7 h-7 text-white group-hover:animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-sky-500 rounded-xl blur opacity-0 group-hover:opacity-30 transition-opacity duration-300"></div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-1 tracking-tight">Human-in-the-Loop Review</h3>
                  <p className="text-sm text-gray-400 font-medium">Expert validation</p>
                </div>
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed text-sm">
                Loop in expert decisions when needed for critical agent actions and performance validation.
              </p>
              <button
                onClick={() => navigate('/feedback-dashboard')}
                className="inline-flex items-center text-blue-400 font-semibold hover:text-blue-300 transition-all duration-300 group-hover:translate-x-1"
              >
                Review System 
                <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Customer Management - Admin Only */}
          {user && user.is_admin && (
            <div className="group relative overflow-hidden rounded-2xl bg-[#ffffff0d] backdrop-blur-xl border border-white/10 hover:border-emerald-500/30 transition-all duration-500 hover:shadow-2xl hover:shadow-emerald-500/20 z-10">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 via-transparent to-green-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative p-8">
                <div className="flex items-center mb-6">
                  <div className="relative">
                    <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center mr-5 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-emerald-500/25">
                      <svg className="w-7 h-7 text-white group-hover:animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500 to-green-500 rounded-xl blur opacity-0 group-hover:opacity-30 transition-opacity duration-300"></div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-1 tracking-tight">Customer Management</h3>
                    <p className="text-sm text-gray-400 font-medium">Admin only</p>
                  </div>
                </div>
                <p className="text-gray-300 mb-6 leading-relaxed text-sm">
                  Manage customer interactions and feedback to optimize agent performance across different segments.
                </p>
                <button
                  onClick={() => navigate('/admin/customers')}
                  className="inline-flex items-center text-emerald-400 font-semibold hover:text-emerald-300 transition-all duration-300 group-hover:translate-x-1"
                >
                  Manage Customers 
                  <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Trusted By Section */}
      <div className="text-center mb-16">
        <h3 className="text-3xl font-bold text-white mb-12 tracking-tight">Trusted by Enablement Teams at</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="group relative overflow-hidden rounded-2xl bg-[#ffffff0d] backdrop-blur-xl border border-white/10 hover:border-purple-500/30 transition-all duration-500 hover:shadow-2xl hover:shadow-purple-500/20 p-8">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative">
              <div className="text-3xl font-bold text-purple-400 mb-3 tracking-tight">Enterprise</div>
              <div className="text-gray-300 font-medium text-lg">Sales & Marketing Teams</div>
            </div>
          </div>
          <div className="group relative overflow-hidden rounded-2xl bg-[#ffffff0d] backdrop-blur-xl border border-white/10 hover:border-blue-500/30 transition-all duration-500 hover:shadow-2xl hover:shadow-blue-500/20 p-8">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-cyan-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative">
              <div className="text-3xl font-bold text-blue-400 mb-3 tracking-tight">SaaS</div>
              <div className="text-gray-300 font-medium text-lg">Product & Technical Teams</div>
            </div>
          </div>
          <div className="group relative overflow-hidden rounded-2xl bg-[#ffffff0d] backdrop-blur-xl border border-white/10 hover:border-emerald-500/30 transition-all duration-500 hover:shadow-2xl hover:shadow-emerald-500/20 p-8">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 via-transparent to-teal-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative">
              <div className="text-3xl font-bold text-emerald-400 mb-3 tracking-tight">Startups</div>
              <div className="text-gray-300 font-medium text-lg">Enablement & Training Teams</div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="group relative overflow-hidden rounded-3xl bg-gradient-to-r from-purple-600 via-blue-600 to-purple-600 p-12 text-white">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 via-transparent to-blue-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
        <div className="relative">
          <h3 className="text-3xl font-bold mb-6 tracking-tight">Ready to Deploy Intelligent Agents?</h3>
          <p className="text-gray-200 mb-8 max-w-2xl mx-auto text-lg leading-relaxed">
            Join teams worldwide who are already using our agent infrastructure to create smarter, self-improving AI systems.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <button
              onClick={() => navigate('/analytics/agents')}
              className="bg-white text-black px-10 py-4 rounded-full font-semibold text-lg hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              Launch Agent Tools
            </button>
            <button
              onClick={() => navigate('/analytics/feedback')}
              className="border-2 border-white text-white px-10 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-black transition-all duration-300 transform hover:scale-105"
            >
              View Feedback System
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main App Component
function App() {
  const [isAppPasswordVerified, setIsAppPasswordVerified] = useState(false);

  const handleAppPasswordVerified = useCallback(() => {
    setIsAppPasswordVerified(true);
  }, []);

  // Show app password protection if not verified
  if (!isAppPasswordVerified) {
    return <AppPasswordProtection onPasswordCorrect={handleAppPasswordVerified} />;
  }

  return (
    <ErrorBoundary>
      <SessionProvider>
        <CreditProvider>
          <ApiKeyProvider>
            <Router>
              <AppLayout>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<HomePage />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/verify-email" element={<EmailVerification />} />
                  <Route path="/reset-password" element={<PasswordReset />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/trust-security" element={<TrustSecurity />} />
                  
                  {/* Protected Routes */}
                  <Route path="/profile" element={
                    <ProtectedRoute>
                      <UserProfile />
                    </ProtectedRoute>
                  } />
                  
                  {/* Console Routes */}
                  <Route path="/console/*" element={
                    <ProtectedRoute>
                      <ConsoleWrapper />
                    </ProtectedRoute>
                  } />
                  
                  {/* Tool Routes */}
                  <Route path="/tools/ppt" element={
                    <ProtectedRoute>
                      <PowerPointGenerator />
                    </ProtectedRoute>
                  } />
                  <Route path="/tools/faq" element={
                    <ProtectedRoute>
                      <FAQGenerator />
                    </ProtectedRoute>
                  } />
                  <Route path="/tools/battlecard" element={
                    <ProtectedRoute>
                      <BattlecardGenerator />
                    </ProtectedRoute>
                  } />
                  <Route path="/tools/architecture" element={
                    <ProtectedRoute>
                      <ArchitectureGenerator />
                    </ProtectedRoute>
                  } />
                  <Route path="/tools/game" element={
                    <ProtectedRoute>
                      <GameMaker />
                    </ProtectedRoute>
                  } />
                  <Route path="/tools/lms" element={
                    <ProtectedRoute>
                      <LMSCourseCreator />
                    </ProtectedRoute>
                  } />
                  <Route path="/tools/sales-sim" element={
                    <ProtectedRoute>
                      <CustomerSalesCallSimulator />
                    </ProtectedRoute>
                  } />
                  <Route path="/tools/prompts" element={
                    <ProtectedRoute>
                      <AgentPromptEditor />
                    </ProtectedRoute>
                  } />
                  
                  {/* Analytics Routes */}
                  <Route path="/analytics/agents" element={
                    <ProtectedRoute>
                      <AgentAnalyticsDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/analytics/customers" element={
                    <ProtectedRoute>
                      <CustomerAnalyticsDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/analytics/feedback" element={
                    <ProtectedRoute>
                      <AgentFeedbackDashboard />
                    </ProtectedRoute>
                  } />
                  
                  {/* Admin Routes */}
                  <Route path="/admin/agents" element={
                    <ProtectedRoute>
                      <AgentManager />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/customers" element={
                    <ProtectedRoute>
                      <CustomerManager />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/webhooks" element={
                    <ProtectedRoute>
                      <WebhookManager />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/api-keys" element={
                    <ProtectedRoute>
                      <UserApiKeyManager />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/self-healing" element={
                    <ProtectedRoute>
                      <AgentSelfHealing />
                    </ProtectedRoute>
                  } />
                  
                  {/* Catch all route */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </AppLayout>
            </Router>
          </ApiKeyProvider>
        </CreditProvider>
      </SessionProvider>
    </ErrorBoundary>
  );
}

export default App; 